import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useVenue } from "./VenueProvider";
import { useAuth } from "./AuthProvider";
import { Badge } from "./badge";
import { showAlert } from "../alertService";
import { logBitacora, BITACORA_ACTIONS } from "../utils/bitacora";
import {
  asignarRecursoAEvento,
  desasignarRecursoDeEvento,
  ESTADO_UNIDAD_LABELS,
} from "../utils/inventario";
import { ocupacionPorUnidad, disponibilidadPool } from "../utils/unidades";
import {
  esItemizado,
  esPool,
  esLineaIncluida,
  piezasReservablesDe,
  unidadDe,
} from "../utils/catalogo";
import {
  WrenchScrewdriverIcon,
  PlusIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";

// Servicios del venue usados en este evento — los del catálogo que llevan
// piezas físicas que reservar. Dos cosas distintas que viven
// juntas porque el owner las hace de una sentada:
//   - ASIGNAR piezas físicas (qué calentador se va a esta boda)
//   - CONSUMIR inventario (cuántas cargas se gastaron)
// La prop `recursos` conserva su nombre: es la lista que EventPage ya arma,
// y el campo del evento sigue siendo `recursosAsignados` (lectura tolerante).
// `soloLectura` (inversionista, P-FRA-13): qué servicios y piezas tiene el
// evento, sin asignar, quitar ni apartar.
const EventServiciosSection = ({ venueId, eventId, event, recursos = [], soloLectura = false }) => {
  const { events } = useVenue();
  const { userInfo, user } = useAuth();

  const [seleccionado, setSeleccionado] = useState("");
  const [unidadesDelRecurso, setUnidadesDelRecurso] = useState([]);
  const [unidadesElegidas, setUnidadesElegidas] = useState([]);
  const [cantidad, setCantidad] = useState("");
  const [saving, setSaving] = useState(false);

  const asignados = useMemo(
    () => (Array.isArray(event?.recursosAsignados) ? event.recursosAsignados : []),
    [event?.recursosAsignados]
  );
  const recursosPorId = useMemo(() => {
    const map = {};
    recursos.forEach((r) => { map[r.id] = r; });
    return map;
  }, [recursos]);

  // Piezas de los servicios YA asignados. "3 piezas asignadas" no contesta la
  // pregunta que el owner tiene el día del evento —CUÁLES calentadores salen—
  // y esa respuesta ya existe en `unidadIds`, solo faltaba resolver sus
  // etiquetas. Se derivan leyendo las piezas de cada servicio asignado (uno o
  // dos por evento), no se copian al asignar: así los eventos que ya existían
  // las muestran igual, y renombrar una pieza se refleja en todos lados.
  const [unidadesPorRecurso, setUnidadesPorRecurso] = useState({});
  const recursosConPiezas = useMemo(
    () =>
      asignados
        .filter((a) => (a.unidadIds || []).length > 0)
        .map((a) => a.recursoId),
    [asignados]
  );
  const clavePiezas = recursosConPiezas.join("|");
  useEffect(() => {
    if (!venueId || recursosConPiezas.length === 0) {
      setUnidadesPorRecurso({});
      return;
    }
    const unsubs = recursosConPiezas.map((rid) =>
      onSnapshot(
        collection(db, "venues", venueId, "conceptos", rid, "unidades"),
        (snap) => {
          const porId = {};
          snap.docs.forEach((d) => {
            porId[d.id] = { id: d.id, ...d.data() };
          });
          setUnidadesPorRecurso((prev) => ({ ...prev, [rid]: porId }));
        },
        (err) => console.error("Unidades asignadas snapshot error:", err)
      )
    );
    return () => unsubs.forEach((u) => u && u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId, clavePiezas]);

  const recurso = seleccionado ? recursosPorId[seleccionado] : null;

  const recursoItemizado = esItemizado(recurso);
  const recursoEsPool = esPool(recurso);

  // Cupo del servicio por cantidad: se lee el doc del pool (la verdad fresca
  // del bloqueo, ver utils/unidades.js) solo del servicio elegido.
  const [reservasPool, setReservasPool] = useState([]);
  useEffect(() => {
    setReservasPool([]);
    if (!venueId || !recurso?.id || !recursoEsPool) return;
    const unsub = onSnapshot(
      doc(db, "venues", venueId, "conceptos", recurso.id, "reservas", "pool"),
      (snap) => setReservasPool(snap.exists() ? snap.data().reservas || [] : []),
      (err) => console.error("Pool snapshot error:", err)
    );
    return unsub;
  }, [venueId, recurso?.id, recursoEsPool]);

  const cupo = useMemo(() => {
    if (!recursoEsPool) return null;
    return disponibilidadPool({
      reservables: piezasReservablesDe(recurso),
      reservas: reservasPool,
      events,
      conceptoId: recurso.id,
      start: event?.startTime,
      end: event?.endTime || event?.startTime,
      excludeEventId: eventId,
    });
  }, [recursoEsPool, recurso, reservasPool, events, event?.startTime, event?.endTime, eventId]);

  // Piezas del servicio elegido. Solo se cargan al elegirlo: no tiene
  // sentido suscribirse a las piezas de todos los servicios del venue.
  useEffect(() => {
    setUnidadesElegidas([]);
    if (!venueId || !recurso?.id || !recursoItemizado) {
      setUnidadesDelRecurso([]);
      return;
    }
    const unsub = onSnapshot(
      collection(db, "venues", venueId, "conceptos", recurso.id, "unidades"),
      (snap) =>
        setUnidadesDelRecurso(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) =>
              (a.etiqueta || "").localeCompare(b.etiqueta || "", "es", { numeric: true })
            )
        ),
      (err) => console.error("Unidades snapshot error:", err)
    );
    return unsub;
  }, [venueId, recurso?.id, recursoItemizado]);

  // Qué piezas ya están tomadas por otro evento que se traslapa. Una sola
  // pasada para todo el picker.
  const ocupadas = useMemo(
    () =>
      ocupacionPorUnidad({
        start: event?.startTime,
        end: event?.endTime || event?.startTime,
        unidadIds: unidadesDelRecurso.map((u) => u.id),
        excludeEventId: eventId,
        events,
      }),
    [event?.startTime, event?.endTime, unidadesDelRecurso, eventId, events]
  );

  const toggleUnidad = (unidadId) => {
    setUnidadesElegidas((prev) =>
      prev.includes(unidadId)
        ? prev.filter((id) => id !== unidadId)
        : [...prev, unidadId]
    );
  };

  const handleAsignar = async () => {
    if (!recurso) {
      showAlert("Elige un servicio.", "error");
      return;
    }
    const cant = Number(cantidad) || 0;
    if (recursoEsPool && !(cant > 0)) {
      showAlert(`¿Cuántas ${unidadDe(recurso)}s se apartan para este evento?`, "error");
      return;
    }
    setSaving(true);
    try {
      const res = await asignarRecursoAEvento({
        venueId,
        eventId,
        item: recurso,
        unidadIds: unidadesElegidas,
        cantidad: cant,
        events,
        user,
      });
      if (!res.ok) {
        // El conflicto de cupo trae su propio mensaje con las cifras; el de
        // pieza identificada se arma con la etiqueta y el evento que la tiene.
        const deCupo = res.conflictos.filter((c) => c.mensaje);
        if (deCupo.length > 0) {
          showAlert(`No se puede apartar. ${deCupo.map((c) => c.mensaje).join(" ")}`, "error");
          return;
        }
        const nombres = res.conflictos
          .map((c) => {
            const u = unidadesDelRecurso.find((x) => x.id === c.unidadId);
            return `${u?.etiqueta || "Una pieza"} (${c.evento?.nombre || "otro evento"})`;
          })
          .join(", ");
        showAlert(`No se puede asignar: ${nombres} ya está en otro evento que se traslapa.`, "error");
        return;
      }
      logBitacora(
        venueId,
        {
          action: BITACORA_ACTIONS.EVENT_SERVICIO_ASSIGN,
          entity: "event",
          entityId: eventId,
          entityName: event?.nombre || "",
          details: {
            recursoId: recurso.id,
            recursoNombre: recurso.nombre,
            unidades: unidadesElegidas.length,
            cantidad: cant,
          },
        },
        userInfo,
        user
      );


      setSeleccionado("");
      setUnidadesElegidas([]);
      setCantidad("");
    } catch (err) {
      showAlert(`No se pudo asignar. [${err.message}]`, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleQuitar = async (asignacion) => {
    try {
      await desasignarRecursoDeEvento({
        venueId,
        eventId,
        recursoId: asignacion.recursoId,
      });
      logBitacora(
        venueId,
        {
          action: BITACORA_ACTIONS.EVENT_SERVICIO_UNASSIGN,
          entity: "event",
          entityId: eventId,
          entityName: event?.nombre || "",
          details: {
            recursoId: asignacion.recursoId,
            recursoNombre: asignacion.recursoNombre,
          },
        },
        userInfo,
        user
      );
      showAlert("Servicio removido · sus costos ya registrados se conservan", "success");
    } catch (err) {
      showAlert(`No se pudo quitar. [${err.message}]`, "error");
    }
  };

  // Los ya asignados siguen en la lista: elegirlos otra vez suma piezas a la
  // asignación existente.
  const yaAsignado = (id) => asignados.some((a) => a.recursoId === id);

  // Lo que las rentas de la cotización traen incluido y LLEVA PIEZAS: es lo
  // único de lo incluido que hay que reservar. Casi nunca pasa —la limpieza de
  // baños no se aparta— pero cuando la renta incluye 100 sillas, alguien tiene
  // que apartarlas o el sábado siguiente se vuelven a prometer.
  //
  // Sale del desglose y no de otra captura: la cotización ya dijo qué lleva
  // este evento y cuántas.
  const porReservar = useMemo(() => {
    const desglose = Array.isArray(event?.desglose) ? event.desglose : [];
    const vistos = new Set();
    return desglose
      .filter((l) => esLineaIncluida(l) && l.conceptoId)
      .map((l) => ({ linea: l, recurso: recursosPorId[l.conceptoId] }))
      .filter(({ recurso }) => recurso && esItemizado(recurso))
      .filter(({ linea }) => !yaAsignado(linea.conceptoId))
      .filter(({ linea }) => {
        if (vistos.has(linea.conceptoId)) return false;
        vistos.add(linea.conceptoId);
        return true;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.desglose, recursosPorId, asignados]);

  // Apartar de un clic lo que la renta incluye: la cantidad ya la dice la
  // línea. Solo aplica al modo por cantidad — en piezas identificadas hay que
  // elegir cuáles, así que ahí se manda al picker de abajo.
  const reservarIncluido = async ({ linea, recurso }) => {
    if (esPool(recurso)) {
      setSaving(true);
      try {
        const res = await asignarRecursoAEvento({
          venueId,
          eventId,
          item: recurso,
          cantidad: Number(linea.units) || Number(linea.cantidadReal) || 1,
          events,
          user,
        });
        if (!res.ok) {
          showAlert(
            `No se puede apartar. ${res.conflictos.map((c) => c.mensaje).filter(Boolean).join(" ")}`,
            "error"
          );
          return;
        }
        logBitacora(
          venueId,
          {
            action: BITACORA_ACTIONS.EVENT_SERVICIO_ASSIGN,
            entity: "event",
            entityId: eventId,
            entityName: event?.nombre || "",
            details: {
              recursoId: recurso.id,
              recursoNombre: recurso.nombre,
              cantidad: Number(linea.units) || 1,
              desdeRenta: true,
            },
          },
          userInfo,
          user
        );
        showAlert("Apartado para este evento", "success");
      } catch (err) {
        showAlert(`No se pudo apartar. [${err.message}]`, "error");
      } finally {
        setSaving(false);
      }
      return;
    }
    setSeleccionado(recurso.id);
  };
  const inputClass =
    "w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]";

  if (recursos.length === 0) return null;

  return (
    <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <div
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: ACCENT + "1a" }}
        >
          <WrenchScrewdriverIcon className="h-4 w-4" style={{ color: ACCENT }} />
        </div>
        <span className="text-sm font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
          Servicios del venue en este evento
        </span>
      </div>

      {asignados.length > 0 && (
        <div className="space-y-2 mb-4">
          {asignados.map((a) => {
            const r = recursosPorId[a.recursoId];
            return (
              <div
                key={a.recursoId}
                className="rounded-lg border border-zinc-200 dark:border-zinc-700/60 px-3 py-2 flex items-start gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-zinc-900 dark:text-white">
                    {a.recursoNombre || r?.nombre || a.recursoId}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-zinc-500">
                    {a.cantidad > 0 && (
                      <Badge color="amber">
                        {a.cantidad} {unidadDe(r)}
                      </Badge>
                    )}
                    {(a.unidadIds || []).length > 0 && (
                      <span>
                        {a.unidadIds.length}{" "}
                        {a.unidadIds.length === 1 ? "pieza asignada" : "piezas asignadas"}
                      </span>
                    )}
                  </div>
                  {/* Cuáles, no solo cuántas. */}
                  {(a.unidadIds || []).length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {a.unidadIds.map((uid) => {
                        const u = unidadesPorRecurso[a.recursoId]?.[uid];
                        const fueraDeServicio = u && (u.estado || "activo") !== "activo";
                        return (
                          <span
                            key={uid}
                            title={
                              u
                                ? [
                                    u.sku ? `SKU ${u.sku}` : null,
                                    fueraDeServicio
                                      ? ESTADO_UNIDAD_LABELS[u.estado] || u.estado
                                      : null,
                                  ]
                                    .filter(Boolean)
                                    .join(" · ") || undefined
                                : "Esta pieza ya no está en el catálogo"
                            }
                            className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset ${
                              !u
                                ? "bg-zinc-50 text-zinc-400 ring-zinc-200 line-through"
                                : fueraDeServicio
                                ? "bg-amber-50 text-amber-700 ring-amber-600/20"
                                : "bg-zinc-100 text-zinc-700 ring-zinc-300 dark:bg-zinc-700 dark:text-zinc-200 dark:ring-zinc-600"
                            }`}
                          >
                            {u?.etiqueta || uid}
                            {fueraDeServicio && (
                              <ExclamationTriangleIcon className="ml-1 h-3 w-3" />
                            )}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                {!soloLectura && (
                <button
                  type="button"
                  onClick={() => handleQuitar(a)}
                  className="text-zinc-400 hover:text-red-500 p-1"
                  title="Quitar del evento"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {soloLectura && asignados.length === 0 && (
        <p className="text-sm text-zinc-400 italic">
          Este evento no tiene servicios del venue asignados.
        </p>
      )}

      {/* Lo que las rentas incluyen y falta apartar */}
      {porReservar.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/20 p-4">
          <div className="flex items-center gap-2 mb-2">
            <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 shrink-0" />
            <div className="text-sm font-semibold text-amber-800 dark:text-amber-300">
              La cotización incluye piezas que no están apartadas
            </div>
          </div>
          <div className="space-y-2">
            {porReservar.map(({ linea, recurso }) => (
              <div
                key={linea.conceptoId}
                className="flex flex-wrap items-center gap-2 rounded-lg bg-white/70 dark:bg-zinc-800/60 px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-zinc-900 dark:text-white truncate">
                    {recurso.nombre}
                  </div>
                  <div className="text-xs text-zinc-500">
                    {Number(linea.units) || 1} {unidadDe(recurso)}
                    {(Number(linea.units) || 1) === 1 ? "" : "s"} · incluidas en la renta
                  </div>
                </div>
                {!soloLectura && (
                <button
                  type="button"
                  onClick={() => reservarIncluido({ linea, recurso })}
                  disabled={saving}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white shadow-sm disabled:opacity-50"
                  style={{ backgroundColor: ACCENT }}
                >
                  {esPool(recurso) ? `Apartar ${Number(linea.units) || 1}` : "Elegir piezas"}
                </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Alta */}
      {!soloLectura && (
      <div className="rounded-lg border border-zinc-100 dark:border-zinc-700/40 bg-zinc-50 dark:bg-zinc-800/30 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-zinc-500 mb-1">
              Servicio
            </label>
            <select
              value={seleccionado}
              onChange={(e) => setSeleccionado(e.target.value)}
              className={inputClass}
            >
              <option value="">Elegir…</option>
              {recursos.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.nombre}
                  {yaAsignado(r.id) ? " (ya asignado)" : ""}
                </option>
              ))}
            </select>
          </div>
        </div>

        <p className="mt-2 text-xs text-zinc-500">
          Reservar las piezas las aparta para este evento. Lo que se gaste en
          ellas —recarga, reposición, mantenimiento— se captura como costo.
        </p>

        {/* Modo por cantidad: se aparta cupo, no piezas con nombre. Enseñar
            cuántas quedan libres ANTES de capturar evita el viaje de ida y
            vuelta al error, y el bloqueo duro lo revalida al guardar. */}
        {recursoEsPool && cupo && (
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                ¿Cuántas se apartan?
              </label>
              <input
                type="number"
                min="1"
                step="1"
                value={cantidad}
                onChange={(e) => setCantidad(e.target.value)}
                placeholder="100"
                className={`${inputClass} max-w-[10rem]`}
              />
            </div>
            <div className="pb-2 text-xs">
              <span
                className={
                  cupo.disponibles > 0
                    ? "font-semibold text-emerald-600"
                    : "font-semibold text-red-600"
                }
              >
                {cupo.disponibles} de {cupo.reservables} disponibles
              </span>
              {cupo.ocupadas > 0 && (
                <span className="text-zinc-500">
                  {" "}
                  · {cupo.ocupadas} apartadas en{" "}
                  {cupo.eventos.length === 1
                    ? cupo.eventos[0].nombre || "otro evento"
                    : `${cupo.eventos.length} eventos que se cruzan`}
                </span>
              )}
              {cupo.sobrevendido > 0 && (
                <div className="mt-0.5 text-amber-600">
                  Hay {cupo.sobrevendido} más apartadas que reservables: revisa el
                  total o las piezas fuera de circulación.
                </div>
              )}
            </div>
          </div>
        )}

        {recursoItemizado && !recursoEsPool && (
          <div className="mt-3">
            <div className="text-xs font-medium text-zinc-500 mb-1.5">
              Piezas
            </div>
            {unidadesDelRecurso.length === 0 ? (
              <p className="text-xs text-zinc-400">
                Este servicio todavía no tiene piezas dadas de alta.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {unidadesDelRecurso.map((u) => {
                  const ocupadaPor = ocupadas[u.id];
                  const fueraDeServicio = (u.estado || "activo") !== "activo";
                  const bloqueada = !!ocupadaPor || fueraDeServicio;
                  const elegida = unidadesElegidas.includes(u.id);
                  return (
                    <button
                      key={u.id}
                      type="button"
                      disabled={bloqueada}
                      onClick={() => toggleUnidad(u.id)}
                      title={
                        ocupadaPor
                          ? `Ya está en ${ocupadaPor.nombre || "otro evento"} en esas fechas`
                          : fueraDeServicio
                          ? ESTADO_UNIDAD_LABELS[u.estado]
                          : undefined
                      }
                      className={`rounded-lg border px-2.5 py-1.5 text-xs font-medium transition ${
                        bloqueada
                          ? "border-zinc-200 bg-zinc-100 text-zinc-400 cursor-not-allowed dark:bg-zinc-800 dark:border-zinc-700"
                          : elegida
                          ? "text-white border-transparent"
                          : "border-zinc-300 text-zinc-600 hover:border-zinc-400 dark:border-zinc-600 dark:text-zinc-300"
                      }`}
                      style={elegida && !bloqueada ? { backgroundColor: ACCENT } : undefined}
                    >
                      {u.etiqueta}
                      {ocupadaPor && (
                        <span className="ml-1 inline-flex items-center">
                          <ExclamationTriangleIcon className="h-3 w-3" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            {Object.keys(ocupadas).length > 0 && (
              <p className="mt-1.5 text-xs text-amber-600">
                Las piezas en gris ya están comprometidas en otro evento que
                se cruza con estas fechas, o están fuera de servicio.
              </p>
            )}
          </div>
        )}

        <div className="mt-3">
          <button
            type="button"
            onClick={handleAsignar}
            disabled={saving || !seleccionado}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: ACCENT }}
          >
            <PlusIcon className="h-4 w-4" />
            {saving ? "Guardando…" : "Asignar al evento"}
          </button>
        </div>
      </div>
      )}
    </div>
  );
};

export default EventServiciosSection;
